import { apiClient } from "./config";
import { Listing, ListingCore, PropertyType } from "@/lib/types";
import { City } from "@/lib/types/locationTypes";
import { AxiosProgressEvent } from "axios";

export const listingWizardService = {
  // Get reference data
  getPropertyTypes: async (): Promise<PropertyType[]> => {
    try {
      const { data } = await apiClient.get("/api/property-types");
      return data.data?.propertyTypes || [];
    } catch (error) {
      console.error("Error fetching property types:", error);
      return [];
    }
  },

  // Get location data
  getProvinces: async () => {
    try {
      const { data } = await apiClient.get("/api/locations/provinces");
      return data.data?.provinces || [];
    } catch (error) {
      console.error("Error fetching provinces:", error);
      return [];
    }
  },

  getDistrictsByProvinceId: async (provinceId: number) => {
    try {
      const { data } = await apiClient.get(
        `/api/locations/provinces/${provinceId}/districts`
      );
      return data.data?.districts || [];
    } catch (error) {
      console.error(
        `Error fetching districts for province ${provinceId}:`,
        error
      );
      return [];
    }
  },

  getCitiesByDistrictId: async (districtId: number) => {
    try {
      const { data } = await apiClient.get(
        `/api/locations/districts/${districtId}/cities`
      );
      return data.data?.cities || [];
    } catch (error) {
      console.error(`Error fetching cities for district ${districtId}:`, error);
      return [];
    }
  },

  getDistrictById: async (districtId: number) => {
    try {
      const { data } = await apiClient.get(
        `/api/locations/districts/${districtId}`
      );
      return data.data?.district || null;
    } catch (error) {
      console.error(`Error fetching district with ID ${districtId}:`, error);
      return null;
    }
  },

  getCityById: async (cityId: number): Promise<City | null> => {
    try {
      const { data } = await apiClient.get(`/api/locations/cities/${cityId}`);
      return data.data?.city || null;
    } catch (error) {
      console.error(`Error fetching city with ID ${cityId}:`, error);
      return null;
    }
  },

  // Keep the old getLocations for now, might be used elsewhere
  getLocations: async () => {
    try {
      const { data } = await apiClient.get("/api/locations");
      return data.data?.locations || [];
    } catch (error) {
      console.error("Error fetching locations:", error);
      return [];
    }
  },

  // Create a draft listing
  createDraftListing: async (initialData: any): Promise<Listing> => {
    console.log("Initial data received by createDraftListing:", initialData); // Log initialData for debugging

    let propertyTypeName = "Unknown Property Type";
    if (initialData.property_type_id) {
      const propertyTypes = await listingWizardService.getPropertyTypes();
      const foundPropertyType = propertyTypes.find(
        (pt) => pt.id === initialData.property_type_id
      );
      if (foundPropertyType) {
        propertyTypeName =
          foundPropertyType.display_name || foundPropertyType.name; // Use display_name if available, otherwise fallback to name
      }
    }

    let cityName = "Unknown City";
    if (initialData.location_id) {
      const city = await listingWizardService.getCityById(
        initialData.location_id
      );
      if (city) {
        cityName = city.display_name;
      }
    }

    const dynamicTitle = `${propertyTypeName} for ${initialData.listing_type} in ${cityName}`;
    const defaultDescription = "Add your description here";

    const payload = {
      ...initialData, // This initialData is already the mapped API data from mapFormToApiData
      // Override title and description as per user feedback
      title: initialData.title || dynamicTitle,
      description: initialData.description || defaultDescription,
    };

    // Clean up undefined values from the payload
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    console.log("Data sent to backend (createDraftListing):", payload);

    try {
      const { data } = await apiClient.post("/api/listings", payload);
      console.log("Response from backend (createDraftListing):", data); // Added console.log
      return data.data; // Return the entire data object, which should be a Listing
    } catch (error) {
      console.error("Error in createDraftListing:", error); // Added error logging
      throw error; // Re-throw the error to be handled by the caller
    }
  },

  // Get a listing by ID (public)
  getListing: async (id: number): Promise<Listing> => {
    const { data } = await apiClient.get(`/api/listings/${id}`);
    return data.data; // Return the entire data object, which includes listing, images, etc.
  },

  // Get a listing draft by ID
  getListingDraft: async (id: number): Promise<Listing> => {
    // Use the general getListing endpoint, as the backend's getListingById returns the full listing including status
    const listing = await listingWizardService.getListing(id);
    // Optionally, you could add a check here if you strictly only want to return drafts
    // if (listing.status !== "draft") {
    //   throw new Error("Listing is not a draft");
    // }
    return listing;
  },

  // Update property details (used for draft updates)
  updatePropertyDetails: async (
    listingId: number,
    details: any
  ): Promise<ListingCore> => {
    console.log("Data sent to backend (updatePropertyDetails):", {
      listingId,
      details,
    }); // Added console.log
    try {
      const { data } = await apiClient.put(
        `/api/listings/${listingId}`,
        details
      );
      console.log("Response from backend (updatePropertyDetails):", data); // Added console.log
      return data.data;
    } catch (error) {
      console.error("Error in updatePropertyDetails:", error); // Added error logging
      throw error; // Re-throw the error to be handled by the caller
    }
  },

  // Update location data (used for draft updates)
  updateLocation: async (
    listingId: number,
    locationData: any
  ): Promise<ListingCore> => {
    const { data } = await apiClient.put(
      `/api/listings/${listingId}`,
      locationData
    );
    return data.data;
  },

  // Update contact info (used for draft updates)
  updateContactInfo: async (
    listingId: number,
    contactInfo: any
  ): Promise<Listing> => {
    const { data } = await apiClient.put(
      `/api/listings/${listingId}`,
      contactInfo
    );
    return data.data;
  },

  // Update title and description (used for draft updates)
  updateTitleAndDescription: async (
    listingId: number,
    titleDesc: { title: string; description: string }
  ): Promise<Listing> => {
    console.log("Data sent to backend (updateTitleAndDescription):", {
      listingId,
      titleDesc,
    }); // Added console.log
    try {
      const { data } = await apiClient.put(
        `/api/listings/${listingId}`,
        titleDesc
      );
      console.log("Response from backend (updateTitleAndDescription):", data); // Added console.log
      return data.data;
    } catch (error) {
      console.error("Error in updateTitleAndDescription:", error); // Added error logging
      throw error; // Re-throw the error to be handled by the caller
    }
  },

  // Upload an image for a draft listing
  uploadImage: async (
    listingId: number,
    imageFile: File,
    isPrimary: boolean = false,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void // Changed type to AxiosProgressEvent
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("image", imageFile);
    if (isPrimary) {
      formData.append("is_primary", "1");
    }

    const { data } = await apiClient.post(
      `/api/listings/drafts/${listingId}/images`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress, // Pass onUploadProgress to axios config
      }
    );

    return data;
  },

  // Delete an image from a draft listing
  deleteImage: async (listingId: number, imageId: number): Promise<any> => {
    const { data } = await apiClient.delete(
      `/api/listings/drafts/${listingId}/images/${imageId}`
    );
    return data;
  },

  // Set an image as primary for a draft listing
  setImageAsPrimary: async (
    listingId: number,
    imageId: number
  ): Promise<any> => {
    const { data } = await apiClient.put(
      `/api/listings/drafts/${listingId}/images/${imageId}/primary`
    );
    return data;
  },

  // Publish listing - changing status from draft to published and updating all data
  publishListing: async (
    listingId: number,
    listingData: any
  ): Promise<Listing> => {
    console.log(
      "[listingWizardService] publishListing: Sending data to backend for listing ID:",
      listingId,
      "Data:",
      listingData
    );
    try {
      // Assuming the backend has a PUT endpoint for full listing updates at /api/listings/:id
      // This endpoint should also handle updating associated images if they are part of the listingData
      const { data } = await apiClient.put(
        `/api/listings/${listingId}`, // Use the general listing update endpoint
        { ...listingData, finalSubmitStatus: "pending" } // Change status to pending for final submission
      );
      console.log(
        "[listingWizardService] publishListing: Backend response:",
        data
      );
      return data.data;
    } catch (error) {
      console.error("[listingWizardService] Error publishing listing:", error);
      throw error;
    }
  },
};
